import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star } from "lucide-react";
import { api, type FontFamily } from "@/lib/api";
import FontFaces, { fontStack } from "@/components/FontFaces";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const SAMPLE = "Boję się, że będę jak moja matka. Żółć gęślą jaźń.";
const FILTERS = [
  { id: "all", label: "wszystkie", category: "", starred: undefined as boolean | undefined },
  { id: "starred", label: "gwiazdki", category: "", starred: true },
  { id: "hand", label: "pisane", category: "hand", starred: undefined },
  { id: "serif", label: "szeryfowe", category: "serif", starred: undefined },
  { id: "sans", label: "bezszeryfowe", category: "sans", starred: undefined },
  { id: "display", label: "ozdobne / wystawowe", category: "display", starred: undefined },
];

function FontCard({
  font,
  onVisible,
  onStar,
}: {
  font: FontFamily;
  onVisible: (id: string) => void;
  onStar: (font: FontFamily) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((row) => row.isIntersecting)) onVisible(font.id);
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [font.id, onVisible]);
  return (
    <Card ref={ref}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium">{font.name}</p>
            <p className="text-[11px] text-muted-foreground">
              {font.category}
              {!font.available ? " · brak pliku na dysku" : ""}
            </p>
          </div>
          <Button
            size="sm"
            variant={font.starred ? "brand" : "outline"}
            onClick={() => onStar(font)}
            title={font.starred ? "Zdejmij gwiazdkę" : "Gwiazdka"}
          >
            <Star className={cn("h-3.5 w-3.5", font.starred ? "fill-current" : "")} />
          </Button>
        </div>
        <p className="text-xl leading-snug" style={{ fontFamily: fontStack(font) }}>
          {SAMPLE}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Fonty() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [ready, setReady] = useState<Set<string>>(() => new Set());
  const active = FILTERS.find((row) => row.id === filter) || FILTERS[0];
  const listQ = useQuery({
    queryKey: ["fonts", active.category, active.starred, q],
    queryFn: () => api.fontsList({ q, category: active.category, starred: active.starred }),
  });
  const starM = useMutation({
    mutationFn: (font: FontFamily) => api.fontPatch(font.id, !font.starred),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fonts"] }),
  });
  const syncM = useMutation({
    mutationFn: () => api.fontsSync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fonts"] }),
  });
  const items = listQ.data?.items || [];
  const visibleFonts = items.filter((row) => ready.has(row.id));
  const onVisible = useCallback((id: string) => {
    setReady((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  return (
    <div>
      <PageHeader
        title="Fonty"
        subtitle="Biblioteka na serwerze. Gwiazdki (w tym pisane) idą na górę pickera w składzie. Ozdobne / wystawowe są na dole."
        actions={
          <Button variant="outline" disabled={syncM.isPending} onClick={() => syncM.mutate()}>
            {syncM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Dociągnij brakujące
          </Button>
        }
      />
      {syncM.data ? (
        <p className="mb-3 text-xs text-muted-foreground">
          Ściągnięte: {syncM.data.downloaded || 0}, już były: {syncM.data.skipped || 0}, brak:{" "}
          {(syncM.data.missing || []).length}
        </p>
      ) : null}
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((row) => (
          <Button key={row.id} size="sm" variant={filter === row.id ? "brand" : "outline"} onClick={() => setFilter(row.id)}>
            {row.label}
          </Button>
        ))}
        <Input className="max-w-xs" placeholder="szukaj…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <FontFaces fonts={visibleFonts} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((font) => (
          <FontCard
            key={font.id}
            font={font}
            onVisible={onVisible}
            onStar={(row) => starM.mutate(row)}
          />
        ))}
      </div>
      {!items.length && !listQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Brak fontów w tym filtrze.</p>
      ) : null}
    </div>
  );
}
